import type { WhitespaceGridCell, WhitespaceGridRow, WhitespaceItem } from 'hooks/useWhitespaceAnalysisGridData';

export type WhitespaceExportScope = 'current_view' | 'opportunity_cells_only' | 'all_filtered_cells';
export type WhitespaceExportFormat = 'xlsx' | 'csv';

type ExportValue = string | number;
type YAxisColumnKey = 'ps_avg_price_per_unit' | 'ps_avg_price_per_eq' | 'ounces' | 'count';

type WhitespaceExportContext = {
    exportScopeLabel: string;
    xAxisValue: string;
    yAxisLabel: string;
    yAxisBucket: string;
    crossSection: string;
    cell: WhitespaceGridCell;
    item?: WhitespaceItem;
};

type WhitespaceExportColumn = {
    key: string;
    header: string;
    getValue: (context: WhitespaceExportContext) => ExportValue;
};

const EMPTY_VALUE = '-';
const normalize = (value?: string) => (value ?? '').trim().toLowerCase();

const SCOPE_LABELS: Record<WhitespaceExportScope, string> = {
    current_view: 'Current View',
    opportunity_cells_only: 'Opportunity Cells Only',
    all_filtered_cells: 'All Filtered Cells',
};

const Y_AXIS_EXPORT_COLUMN_KEYS = new Set<YAxisColumnKey>(['ps_avg_price_per_unit', 'ps_avg_price_per_eq', 'ounces', 'count']);
const Y_AXIS_COLUMN_BY_KPI_ID: Record<string, YAxisColumnKey> = {
    ps_avg_price_per_unit: 'ps_avg_price_per_unit',
    ps_avg_price_per_eq: 'ps_avg_price_per_eq',
    ounces: 'ounces',
    ounce: 'ounces',
    count: 'count',
};

const getHierarchyValue = (item: WhitespaceItem | undefined, attributeTypes: string[]) => {
    if (!item?.hierarchy?.length) return EMPTY_VALUE;
    const typeSet = new Set(attributeTypes.map(normalize));
    const match = item.hierarchy.find((entry) => typeSet.has(normalize(entry.attribute_type)));
    return match?.description ?? match?.ex_id ?? EMPTY_VALUE;
};

const getKpiValue = (item: WhitespaceItem | undefined, kpiIds: string[]) => {
    if (!item?.kpis?.length) return EMPTY_VALUE;
    const kpiIdSet = new Set(kpiIds.map(normalize));
    const match = item.kpis.find((kpi) => kpiIdSet.has(normalize(kpi.kpi_id)));
    if (!match || match.value == null) return EMPTY_VALUE;
    return `${match.valuePrefix ?? ''}${String(match.value)}${match.valueSuffix ?? ''}`;
};

const getSelectedYAxisColumnKey = (selectedYAxisKpiId?: string): YAxisColumnKey =>
    Y_AXIS_COLUMN_BY_KPI_ID[normalize(selectedYAxisKpiId)] ?? 'ps_avg_price_per_unit';

const getYAxisLabelFromKey = (key: YAxisColumnKey) => {
    const labelByKey: Record<YAxisColumnKey, string> = {
        ps_avg_price_per_unit: 'Price per Unit',
        ps_avg_price_per_eq: 'Price per EQ',
        ounces: 'Ounces',
        count: 'Count',
    };
    return labelByKey[key];
};

const COLUMN_MAP: Record<string, WhitespaceExportColumn> = {
    export_scope: { key: 'export_scope', header: 'Export Scope', getValue: ({ exportScopeLabel }) => exportScopeLabel },
    cell_id: {
        key: 'cell_id',
        header: 'Cell ID',
        getValue: ({ xAxisValue, yAxisLabel, yAxisBucket }) => `${xAxisValue || EMPTY_VALUE}_${yAxisLabel}_${yAxisBucket || EMPTY_VALUE}`,
    },
    x_axis_value: { key: 'x_axis_value', header: 'X-Axis Value', getValue: ({ xAxisValue }) => xAxisValue || EMPTY_VALUE },
    y_axis_bucket: { key: 'y_axis_bucket', header: 'Y-Axis Bucket', getValue: ({ yAxisBucket }) => yAxisBucket || EMPTY_VALUE },
    cross_section: { key: 'cross_section', header: 'Cross Section', getValue: ({ crossSection }) => crossSection || EMPTY_VALUE },
    entity_name: { key: 'entity_name', header: 'Entity Name', getValue: ({ item }) => item?.description ?? item?.ex_id ?? EMPTY_VALUE },
    vendor: { key: 'vendor', header: 'Vendor', getValue: ({ item }) => getHierarchyValue(item, ['vendor']) },
    brand: { key: 'brand', header: 'Brand', getValue: ({ item }) => getHierarchyValue(item, ['brand']) },
    entity_type: { key: 'entity_type', header: 'Entity Type', getValue: ({ item }) => (item?.is_own_attribute ? 'Own' : 'Competitor') },
    ps_avg_price_per_unit: { key: 'ps_avg_price_per_unit', header: 'Price per Unit', getValue: ({ item }) => getKpiValue(item, ['ps_avg_price_per_unit']) },
    ps_avg_price_per_eq: { key: 'ps_avg_price_per_eq', header: 'Price per EQ', getValue: ({ item }) => getKpiValue(item, ['ps_avg_price_per_eq']) },
    ounces: { key: 'ounces', header: 'Ounces', getValue: ({ item }) => getKpiValue(item, ['ounces', 'ounce']) },
    count: { key: 'count', header: 'Count', getValue: ({ cell }) => cell.crossSectionShareLabel?.split('/')[0] ?? EMPTY_VALUE },
    total_value: { key: 'total_value', header: 'Total Value', getValue: ({ cell }) => cell.raw_value != null ? `${cell.valuePrefix ?? ''}${cell.raw_value}` : EMPTY_VALUE },
    growth_pct: { key: 'growth_pct', header: 'Growth %', getValue: ({ cell }) => (typeof cell.trendValue === 'number' ? `${Math.abs(cell.trendValue)}%` : EMPTY_VALUE) },
    share_pct: { key: 'share_pct', header: 'Share %', getValue: ({ cell }) => cell.shareLabel || EMPTY_VALUE },
};

// Order + visibility control for export columns.
export const WHITESPACE_EXPORT_COLUMN_KEYS: string[] = [
    'export_scope',
    'cell_id',
    'x_axis_value',
    'y_axis_bucket',
    'cross_section',
    'entity_name',
    'vendor',
    'brand',
    'entity_type',
    'ps_avg_price_per_unit',
    'ps_avg_price_per_eq',
    'ounces',
    'count',
    'total_value',
    'growth_pct',
    'share_pct',
];

const resolveExportColumns = (selectedYAxisKpiId?: string) => {
    const selectedYAxisColumnKey = getSelectedYAxisColumnKey(selectedYAxisKpiId);
    return WHITESPACE_EXPORT_COLUMN_KEYS
        .map((columnKey) => COLUMN_MAP[columnKey])
        .filter((column): column is WhitespaceExportColumn => Boolean(column))
        .filter((column) => !Y_AXIS_EXPORT_COLUMN_KEYS.has(column.key as YAxisColumnKey) || column.key === selectedYAxisColumnKey);
};

export const buildWhitespaceExportRows = ({
    gridRows,
    columnHeaders,
    crossSection,
    scope,
    selectedYAxisKpiId,
}: {
    gridRows: WhitespaceGridRow[];
    columnHeaders: string[];
    crossSection: string;
    scope: WhitespaceExportScope;
    selectedYAxisKpiId?: string;
}) => {
    const selectedYAxisColumnKey = getSelectedYAxisColumnKey(selectedYAxisKpiId);
    const exportColumns = resolveExportColumns(selectedYAxisKpiId);
    const headers = exportColumns.map((column) => column.header);
    const exportScopeLabel = SCOPE_LABELS[scope];
    const yAxisLabel = getYAxisLabelFromKey(selectedYAxisColumnKey);
    const rows: Array<Array<ExportValue>> = [];

    gridRows.forEach((gridRow) => {
        columnHeaders.forEach((columnHeader) => {
            const cell = gridRow.cells[columnHeader];
            if (!cell || (scope === 'opportunity_cells_only' && !cell.isWhitespace)) return;

            const items = cell.items?.length ? cell.items : [undefined];
            items.forEach((item) => {
                const context: WhitespaceExportContext = {
                    exportScopeLabel,
                    xAxisValue: columnHeader,
                    yAxisLabel,
                    yAxisBucket: gridRow.bucketLabel,
                    crossSection,
                    cell,
                    item,
                };
                rows.push(exportColumns.map((column) => column.getValue(context)));
            });
        });
    });

    return { headers, rows };
};

